import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, TicketPercent, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { CatalogTable } from '@/features/catalog/Catalog';
import { contentTypesList } from '@/shared/api/generated/content/content';
import type { PromoBody, ShopSettingsBody } from '@/shared/api/generated/model';
import { recordsList } from '@/shared/api/generated/records/records';
import {
  shopPromoCreate,
  shopPromoDelete,
  shopPromos,
  shopPromoUpdate,
} from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DatePicker } from '@/shared/ui/DatePicker';
import { ConfirmDialog, Dialog, DialogContent } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { MultiPicker } from '@/shared/ui/MultiPicker';
import { Segmented } from '@/shared/ui/Segmented';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';
import { money, promoState, promoValue } from './shop';

/**
 * Промокоды (шаг 9.7): свои у окружения, переносом не едут. Код — без учёта
 * регистра; использование засчитывается заказом и возвращается, если
 * неоплаченный заказ отменили.
 */
export function PromosTab({
  siteId,
  env,
  shop,
}: {
  siteId: string;
  env: EnvKey;
  shop: ShopSettingsBody;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PromoBody | 'new'>();
  const [removing, setRemoving] = useState<PromoBody>();
  const key = ['shop-promos', siteId, env];

  const promos = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await shopPromos(siteId, kinds[env]);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список промокодов');
      }

      return response.data.items ?? [];
    },
  });

  const items = promos.data ?? [];
  const create = () => {
    setEditing('new');
  };

  return (
    <div className={styles['list']}>
      {promos.isPending ? null : items.length === 0 ? (
        <EmptyState
          icon={<TicketPercent />}
          title="Промокодов пока нет"
          description="Процент или сумма, на весь заказ или на категории, с порогом, сроком и лимитом. Код вводят на сайте — Faceta сама проверит и посчитает скидку."
          action={<Button onClick={create}>Завести промокод</Button>}
        />
      ) : (
        <>
          <div>
            <Button iconLeft={<Plus size={15} />} onClick={create}>
              Новый промокод
            </Button>
          </div>
          <CatalogTable head={['Код', 'Скидка', 'Условия', 'Использован', '']}>
            {items.map((p) => {
              const state = promoState(p);

              return (
                <tr key={p.id}>
                  <td className={styles['mono']}>{p.code}</td>
                  <td>{promoValue(p)}</td>
                  <td>{conditions(p)}</td>
                  <td>
                    {p.used_count}
                    {p.usage_limit === undefined ? '' : ` из ${String(p.usage_limit)}`}
                  </td>
                  <td>
                    <div className={styles['panel__actions']}>
                      {state !== undefined && (
                        <Badge size="sm" tone={state.tone}>
                          {state.label}
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        iconOnly
                        aria-label="Изменить"
                        onClick={() => {
                          setEditing(p);
                        }}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        iconOnly
                        aria-label="Удалить"
                        onClick={() => {
                          setRemoving(p);
                        }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </CatalogTable>
        </>
      )}

      {editing !== undefined && (
        <PromoDialog
          siteId={siteId}
          env={env}
          shop={shop}
          promo={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(undefined);
          }}
          onSaved={() => {
            setEditing(undefined);
            void queryClient.invalidateQueries({ queryKey: key });
          }}
        />
      )}

      <ConfirmDialog
        open={removing !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(undefined);
          }
        }}
        title={`Удалить промокод ${removing?.code ?? ''}?`}
        description="На сайте его больше не примут. Заказы, оформленные с ним, остаются — код в них записан."
        confirmLabel="Удалить"
        tone="danger"
        onConfirm={async () => {
          if (removing === undefined) {
            return;
          }

          try {
            await shopPromoDelete(removing.id);
            setRemoving(undefined);
            void queryClient.invalidateQueries({ queryKey: key });
          } catch (cause) {
            toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось удалить');
          }
        }}
      />
    </div>
  );
}

function conditions(p: PromoBody): string {
  const parts: string[] = [];

  if (p.min_total > 0) {
    parts.push(`от ${money(p.min_total)}`);
  }

  if ((p.category_ids ?? []).length > 0 || (p.product_ids ?? []).length > 0) {
    parts.push('на часть товаров');
  }

  if (p.once_per_customer) {
    parts.push('раз на покупателя');
  }

  if (p.stackable) {
    parts.push('с личной скидкой');
  }

  if (p.ends_at !== undefined) {
    parts.push(`до ${new Date(p.ends_at).toLocaleDateString('ru-RU')}`);
  }

  return parts.length === 0 ? 'на весь заказ' : parts.join(' · ');
}

/** Дата из ISO в «YYYY-MM-DD» по местному времени и обратно. */
function dayOf(iso: string | undefined): string {
  if (iso === undefined) {
    return '';
  }

  const d = new Date(iso);

  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isoOf(day: string, endOfDay: boolean): string | undefined {
  if (day === '') {
    return undefined;
  }

  const [y, m, d] = day.split('-').map(Number);

  return new Date(
    y ?? 0,
    (m ?? 1) - 1,
    d ?? 1,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
  ).toISOString();
}

function useRecords(typeId: string | undefined) {
  return useQuery({
    queryKey: ['records-picker', typeId],
    enabled: typeId !== undefined,
    queryFn: async () => {
      const response = await recordsList(typeId ?? '', { per_page: 100 });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список записей');
      }

      return (response.data.items ?? []).map((r) => ({
        value: r.id,
        label: r.title || 'Без названия',
      }));
    },
  });
}

function PromoDialog({
  siteId,
  env,
  shop,
  promo,
  onClose,
  onSaved,
}: {
  siteId: string;
  env: EnvKey;
  shop: ShopSettingsBody;
  promo: PromoBody | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(promo?.code ?? '');
  const [kind, setKind] = useState<'percent' | 'amount'>(promo?.kind ?? 'percent');
  const [value, setValue] = useState(promo === undefined ? '' : String(promo.value));
  const [minTotal, setMinTotal] = useState(
    promo === undefined || promo.min_total === 0 ? '' : String(promo.min_total),
  );
  const [startsAt, setStartsAt] = useState(dayOf(promo?.starts_at));
  const [endsAt, setEndsAt] = useState(dayOf(promo?.ends_at));
  const [limit, setLimit] = useState(
    promo?.usage_limit === undefined ? '' : String(promo.usage_limit),
  );
  const [once, setOnce] = useState(promo?.once_per_customer ?? false);
  const [stackable, setStackable] = useState(promo?.stackable ?? false);
  const [enabled, setEnabled] = useState(promo?.enabled ?? true);
  const [products, setProducts] = useState<string[]>(promo?.product_ids ?? []);
  const [categories, setCategories] = useState<string[]>(promo?.category_ids ?? []);
  const [note, setNote] = useState(promo?.note ?? '');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const productItems = useRecords(shop.products?.id);
  const categoriesType = useQuery({
    queryKey: ['shop-categories-type', siteId, env],
    queryFn: async () => {
      const response = await contentTypesList(siteId, kinds[env], {});

      return response.status === 200
        ? (response.data.items ?? []).find((t) => t.slug === 'categories')?.id
        : undefined;
    },
  });
  const categoryItems = useRecords(categoriesType.data ?? undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const amount = Number(value.replace(',', '.'));
    const min = minTotal.trim() === '' ? 0 : Number(minTotal.replace(',', '.'));
    const usage = limit.trim() === '' ? undefined : Number(limit);

    if (!(amount > 0) || (kind === 'percent' && amount > 100)) {
      setError(kind === 'percent' ? 'Процент — от 0 до 100' : 'Сумма — больше нуля');

      return;
    }

    if (!(min >= 0) || (usage !== undefined && !(Number.isInteger(usage) && usage > 0))) {
      setError('Порог — число от 0, лимит — целое число больше нуля или пусто');

      return;
    }

    const starts = isoOf(startsAt, false);
    const ends = isoOf(endsAt, true);
    const body = {
      code: code.trim(),
      kind,
      value: amount,
      min_total: min,
      ...(starts === undefined ? {} : { starts_at: starts }),
      ...(ends === undefined ? {} : { ends_at: ends }),
      ...(usage === undefined ? {} : { usage_limit: usage }),
      once_per_customer: once,
      stackable,
      enabled,
      product_ids: products,
      category_ids: categories,
      note: note.trim(),
    };

    setBusy(true);
    setError(undefined);

    (promo === undefined
      ? shopPromoCreate(siteId, kinds[env], body)
      : shopPromoUpdate(promo.id, body)
    )
      .then(() => {
        toast.success(promo === undefined ? 'Промокод заведён' : 'Сохранено');
        onSaved();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось сохранить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={promo === undefined ? 'Новый промокод' : `Промокод ${promo.code}`}
        size="md"
        dismissible={false}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" form="promo-form" loading={busy}>
              Сохранить
            </Button>
          </>
        }
      >
        <form id="promo-form" className={styles['form']} onSubmit={submit} noValidate>
          <div className={styles['row']}>
            <Field label="Код" hint="Без учёта регистра: welcome = WELCOME">
              <Input
                mono
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
              />
            </Field>
            <Field label={kind === 'percent' ? 'Скидка, %' : 'Скидка, ₽'}>
              <div className={styles['copy']}>
                <Input
                  inputMode="decimal"
                  value={value}
                  onChange={(event) => {
                    setValue(event.target.value);
                  }}
                />
                <Segmented
                  value={kind}
                  aria-label="Вид скидки"
                  onValueChange={(next) => {
                    setKind(next as typeof kind);
                  }}
                  options={[
                    { value: 'percent', label: '%' },
                    { value: 'amount', label: '₽' },
                  ]}
                />
              </div>
            </Field>
          </div>

          <div className={styles['row']}>
            <Field label="Действует с">
              <DatePicker value={startsAt} onChange={setStartsAt} placeholder="сразу" />
            </Field>
            <Field label="Действует до">
              <DatePicker value={endsAt} onChange={setEndsAt} placeholder="без срока" />
            </Field>
          </div>

          <div className={styles['row']}>
            <Field label="От суммы, ₽" hint="Считаются только товары, на которые действует код">
              <Input
                inputMode="decimal"
                placeholder="без порога"
                value={minTotal}
                onChange={(event) => {
                  setMinTotal(event.target.value);
                }}
              />
            </Field>
            <Field
              label="Сколько раз всего"
              hint={promo === undefined ? undefined : `Использован: ${String(promo.used_count)}`}
            >
              <Input
                inputMode="numeric"
                placeholder="без ограничения"
                value={limit}
                onChange={(event) => {
                  setLimit(event.target.value);
                }}
              />
            </Field>
          </div>

          <Field label="На какие товары" hint="Ничего не выбрано — на весь заказ">
            <div className={styles['panel__actions']}>
              <MultiPicker
                label={
                  categories.length === 0 ? 'Категории' : `Категории: ${String(categories.length)}`
                }
                items={categoryItems.data ?? []}
                selected={categories}
                onChange={setCategories}
                emptyText="Категорий нет"
              />
              <MultiPicker
                label={products.length === 0 ? 'Товары' : `Товары: ${String(products.length)}`}
                items={productItems.data ?? []}
                selected={products}
                onChange={setProducts}
                emptyText="Товаров нет"
              />
            </div>
          </Field>

          <Switch checked={once} onCheckedChange={setOnce}>
            Один раз на покупателя — по входу или почте
          </Switch>
          <Switch checked={stackable} onCheckedChange={setStackable}>
            Суммируется с личной скидкой — иначе покупателю достаётся бо́льшая выгода
          </Switch>
          <Switch checked={enabled} onCheckedChange={setEnabled}>
            Включён
          </Switch>

          <Field label="Заметка" hint="Для команды: откуда код, для кого" error={error}>
            <Input
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}
